import React from 'react';
import { Translate, TranslateContext, withTranslate } from 'core/translate';
import { Button, ButtonIcon, ButtonText } from 'core/controls';
import { getCallToActions } from '../resources/shoppingLinks/shoppingHelper.constant';
import ScrollTop from '../common/ScrollTop';
import InventorySummary from './InventorySummary';
import { computePricing } from '../common/pricing/pricing';
import { Link, Route } from 'react-router-dom';
import MathBox from '../components/mathBox/MathBox';
import SummaryAccordion from './SummaryAccordion';
import SummaryNav from './SummaryNav';
import SpeedBump from './SpeedBump';
import { computeAccessories } from './utils';
import PaymentEstimatorModal from './paymentEstimator/PaymentEstimatorModal';
import { withModelInfo } from '../common/ModelInfoContext';
import Share from './share/Share';
import MathBoxOptions from './MathBoxOptions';
import { getQueryStringValue } from '../common/routing/queryStringHelpers';

const ContactRepSection = ({ url, enabled }) => {
  if (!enabled) {
    return null;
  }
  return (
    <div>
      <Button
        as="a"
        primary
        href={url}
        className="button--flex contact-rep-button">
        <ButtonIcon name="locate" />
        <ButtonText>
          <Translate value="byo.cta.label.contact.rep" />
        </ButtonText>
      </Button>
    </div>
  );
};

const SummaryTopButtonsSection = () => {
  return (
    <div className="summary-button-container">
      <Button
        as={Link}
        analytics="button"
        to="/summary/share"
        className="button--flex button--link button--summary-link summary-button-container__button"
        data-dtm="summary"
        id="button-send">
        <ButtonIcon name="send" />
        <ButtonText>
          <Translate value="send" />
        </ButtonText>
      </Button>
      <Button
        analytics="button"
        className="button--flex button--link button--summary-link summary-button-container__button"
        data-dtm="summary"
        onClick={() => {
          window.print();
        }}>
        <ButtonIcon name="print" />
        <ButtonText>
          <Translate value="byo.page.general.print" />
        </ButtonText>
      </Button>
    </div>
  );
};

const SummaryBottomButtonsSection = ({ onStartOver }) => {
  return (
    <TranslateContext.Consumer>
      {translate => (
        <div className="summary-button-container">
          <Button
            analytics="text"
            className="button--flex button--link button--summary-link"
            data-dtm={translate('component.common.start.over')}
            onClick={onStartOver}
            id="start-over">
            <ButtonIcon name="refresh" />
            <ButtonText>
              <Translate value="component.common.start.over" />
            </ButtonText>
          </Button>
        </div>
      )}
    </TranslateContext.Consumer>
  );
};

class Summary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      speedBumpCta: null
    };
  }

  openSpeedBump = cta => {
    this.setState({
      speedBumpCta: cta
    });
  };

  closeSpeedBump = () => {
    this.setState({
      speedBumpCta: null
    });
  };

  render() {
    const {
      translate,
      state,
      gmwcState,
      standardFeatures,
      isLoadingStandardFeatures,
      modelInfo,
      onStartOver,
      inventoryCount,
      vsParams,
      enableVehicleShopper,
      enableInventorySummary,
      enableInventorySummaryCount
    } = this.props;

    const { speedBumpCta } = this.state;
    const { config, params, incentives, appResources, contactRepUrl } = state;
    const {
      isFleet,
      isMobile,
      enableFinancePayments,
      enableLeasePayments
    } = modelInfo;
    const { style, msrp, eligibleForInvoicePrice } = config;

    const isFleetMobile = isFleet && isMobile;
    const pricing = computePricing(msrp, incentives);

    const ctas = getCallToActions(
      { ...state, inventory: { count: inventoryCount } },
      translate,
      pricing,
      gmwcState,
      modelInfo,
      { vsParams, enableVehicleShopper }
    );

    return (
      <ScrollTop>
        <div className="hide-for-print">
          <InventorySummary
            enabled={enableInventorySummary}
            cta={ctas[0]}
            count={inventoryCount}
            showSpeedBump={this.openSpeedBump}
            showCount={enableInventorySummaryCount}
          />
          <ContactRepSection url={contactRepUrl} enabled={isFleetMobile} />
          <SummaryTopButtonsSection />
          <div>
            <div className="summary__math-box">
              <MathBox
                isFamilyFirstUS={modelInfo.isFamilyFirstUS}
                isFamilyFirstCA={modelInfo.isFamilyFirstCA}
                pricing={pricing}
                country={params.country}
                eventName={style.vehicleInfo.promoEventName}
                isSummary={true}
                eligibleForInvoicePrice={eligibleForInvoicePrice}>
                <MathBoxOptions pricing={pricing} config={config} />
              </MathBox>
            </div>
            <div className="show-for-sr">
              <span>† {pricing.incentives.disclaimer}</span>
            </div>
          </div>
          <SummaryAccordion
            accessories={computeAccessories(config)}
            displayIpe={
              appResources.display.showPaymentEstimatorComponent &&
              !pricing.isSeeDealer
            }
            showFinance={enableFinancePayments && gmwcState.fshowsum}
            showLease={enableLeasePayments && gmwcState.lshowsum}
            standardFeatures={standardFeatures}
            loadingFeatures={isLoadingStandardFeatures}
          />
          <SummaryNav ctas={ctas} onShowSpeedBump={this.openSpeedBump} />
          <SummaryBottomButtonsSection onStartOver={onStartOver} />
        </div>
        <SpeedBump
          onClose={this.closeSpeedBump}
          cta={speedBumpCta}
          isFamilyFirstCA={modelInfo.isFamilyFirstCA}
          isFamilyFirstUS={modelInfo.isFamilyFirstUS}
        />
        <Route
          path="/summary/payment"
          render={({ location, history }) => {
            const initialTab = getQueryStringValue(
              location.search,
              'initialTab'
            );

            return (
              <PaymentEstimatorModal
                bodyStyleTitle={modelInfo.title}
                params={params}
                config={config}
                defaultTabIndex={initialTab === 'lease' ? 1 : 0}
                onClose={() => {
                  history.push('/summary');
                }}
                urls={appResources.urls}
              />
            );
          }}
        />
        <Route path="/summary/share" render={() => <Share />} />
      </ScrollTop>
    );
  }
}

export default withModelInfo(withTranslate(Summary));
